import { useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import { Paper } from "@mui/material"
import { chain, map, find, keys, capitalize, omitBy } from "lodash"
import Table from "components/material/table"
import { RESOURCE, PERMISSION, UUID, ROLE } from "helpers/shapes"
import { upsellingTitle } from "helpers/op-pro"
import { PERMISSION_LEVELS, PERMISSION_LEVELS_AS_OPTIONS, hasPermission } from "helpers/permissions"
import { getResourceLabel } from "helpers/permissions/resource-types"
import UpsellTabs, { UPSELL_TABS_SHAPE } from "components/material/upsell-tabs"

export default function MemberPermissions({ account, role, memberId, permissions, resources, update, userId, isOpPro, datasetEntitlementsEnabled, tabs }) {
  const isAdmin = role === "admin"
  const nonEditable = isOpPro

  const getResourceType = useCallback((resourceId) => {
    return chain(resources)
      .find(({ id }) => id === resourceId)
      .get("type")
      .value()
  }, [resources])

  const optionLabels = useMemo(() =>
    chain(resources)
      .keyBy("id")
      .mapValues(getResourceLabel)
      .value(),
  [resources])

  const availableOptionLabels = useMemo(() =>
    omitBy(optionLabels, (_, id) => find(permissions, p => p.id === id)),
  [permissions, optionLabels])

  const addForm = useMemo(() => {
    if (!isAdmin || nonEditable) { return null }

    function onAdd(data, cb) {
      const query = `mutation ($memberId: IdOrEmail!, $permissionLevel: PermissionLevel!) {
        ${map(data.resources, (resourceId, index) => `resource${index}: updatePermissions(resourceType: ${getResourceType(resourceId)}, resourceId: "${resourceId}", permissions: { memberId: $memberId, permissionLevel: $permissionLevel }) { id }`)}
      }`

      const variables = { permissionLevel: data.permissionLevel, memberId }

      update(query, variables, account, {
        reloadData: "memberPermissions",
      }, cb)
    }

    return {
      title: "Add Permissions",
      submitText: "Add Permissions",
      track: {
        type: "member_permission_added",
        payload: ({ resources, permissionLevel }) => ({
          resources: map(resources, id => optionLabels[id]),
          permissionLevel,
        }),
      },
      onSubmit: onAdd,
      fields:
      [{
        id: "resources",
        label: "Resources",
        type: "multi",
        options: keys(availableOptionLabels),
        optionLabels: availableOptionLabels,
        validations: {
          isRequired: true,
        },
        sort: ([, lbl1], [, lbl2]) => lbl1.localeCompare(lbl2),
      }, {
        id: "permissionLevel",
        label: "Permission Level",
        type: "select",
        options: keys(PERMISSION_LEVELS),
        optionLabels: PERMISSION_LEVELS_AS_OPTIONS,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [isAdmin, nonEditable, availableOptionLabels, memberId, update, account, getResourceType, optionLabels])

  const editForm = useMemo(() => {
    if (nonEditable) { return null }

    function onEdit(data, cb) {
      const query = `mutation ($memberId: IdOrEmail!, $resourceId: IdOrShortIdOrAlias!, $resourceType: ResourceType!, $permissionLevel: PermissionLevel!) {
        updatePermissions(resourceType: $resourceType, resourceId: $resourceId, permissions: { memberId: $memberId, permissionLevel: $permissionLevel }) { id }
      }`

      const variables = {
        resourceId: data.id,
        resourceType: getResourceType(data.id),
        permissionLevel: data.permissionLevel,
        memberId,
      }

      update(query, variables, account, {
        reloadData: "memberPermissions",
      }, cb)
    }

    return {
      title: "Edit Permission Level",
      submitText: "Save",
      track: {
        type: "member_permission_level_edited",
        payload: ({ id, permissionLevel }) => ({
          resource: optionLabels[id],
          permissionLevel,
        }),
      },
      isEnabled: ({ permissionLevel }) => isAdmin || (userId === memberId && hasPermission(permissionLevel, PERMISSION_LEVELS.OWNER)),
      onSubmit: onEdit,
      getInitialValues: ({ permissionLevel }) => ({ permissionLevel }),
      fields: [{
        id: "permissionLevel",
        label: "Permission Level",
        type: "select",
        options: keys(PERMISSION_LEVELS),
        optionLabels: PERMISSION_LEVELS_AS_OPTIONS,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [account, isAdmin, memberId, userId, update, getResourceType, optionLabels, nonEditable])

  const deleteForm = useMemo(() => {
    if (nonEditable) { return null }

    function onDelete(data, cb) {
      const query = `mutation ($memberId: IdOrEmail!) {
        ${map(data, (resource, index) => `resource${index}: updatePermissions(resourceType: ${resource.type}, resourceId: "${resource.id}", permissions: { memberId: $memberId, permissionLevel: NONE }) { id }`)}
      }`

      const variables = { memberId }

      update(query, variables, account, {
        reloadData: "memberPermissions",
      }, cb)
    }

    return {
      type: "permission",
      track: {
        type: "remove_member_permission",
        payload: ({ id }) => ({ resource: optionLabels[id] }),
      },
      isEnabled: ({ permissionLevel }) => isAdmin || (userId === memberId && hasPermission(permissionLevel, PERMISSION_LEVELS.OWNER)),
      renderItem: resource => <li key={resource.id}>{resource.name}</li>,
      onSubmit: onDelete,
    }
  }, [account, isAdmin, memberId, update, userId, optionLabels, nonEditable])

  return <div className="modules_member-resources">
    {datasetEntitlementsEnabled && <UpsellTabs id="member-resources" tabs={tabs} />}
    <Paper className="content" square>
      <Table
        title="Member Permissions"
        idField="id"
        addForm={addForm}
        addDisabledText={upsellingTitle(isOpPro, "members")}
        editForm={editForm}
        deleteForm={deleteForm}
        data={permissions}
        columns={[{
          name: "Name",
          id: "name",
        }, {
          name: "Type",
          id: "type",
          formatter: capitalize,
        }, {
          name: "Description",
          id: "description",
        }, {
          name: "Permission Level",
          id: "permissionLevel",
          formatter: capitalize,
        }]}
      />
    </Paper>
  </div>
}

MemberPermissions.propTypes = {
  account: PropTypes.string.isRequired,
  memberId: UUID,
  userId: UUID,
  role: ROLE.isRequired,
  permissions: PropTypes.arrayOf(PERMISSION),
  resources: PropTypes.arrayOf(RESOURCE).isRequired,
  update: PropTypes.func.isRequired,
  isOpPro: PropTypes.bool.isRequired,
  datasetEntitlementsEnabled: PropTypes.bool.isRequired,
  tabs: UPSELL_TABS_SHAPE,
}
